import { faCalendar, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { format } from "date-fns";
import { twJoin } from "tailwind-merge";

import {
  DateTimePicker,
  DateTimePickerProps,
} from "src/base-components/DateTimePicker";
import { useIsInFloatingWindow } from "src/base-components/FloatingWindow/FloatingWindow";
import { CustomPopover as Popover } from "src/base-components/Popover";
import { Icon, IconButton } from "src/design-system/Icon";
import { FIELD_BORDER, FIELD_TEXT } from "src/design-system/utils/fieldStyles";

export type DateTimePickerDropdownProps = {
  placeholder?: string;
  borderless?: boolean;
  errored?: boolean;
  hinted?: boolean;
  fullWidth?: boolean;
  onReset?: () => void;
} & DateTimePickerProps;

export const DateTimePickerDropdown = ({
  disabled = false,
  value,
  placeholder = "Pick a date and time",
  borderless = false,
  errored = false,
  hinted = false,
  dateOnly = false,
  fullWidth = false,
  onReset,
  ...props
}: DateTimePickerDropdownProps) => {
  const isInFloatingWindow = useIsInFloatingWindow();
  const formatString = dateOnly ? "MMM dd, yyyy" : "MMM dd, yyyy hh:mm a";

  const button = (
    <div
      className={twJoin(
        "flex h-8 w-full items-center gap-x-1 whitespace-nowrap rounded-lg px-2 py-1 text-left font-inter-normal-12px text-gray-800 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/25",
        !borderless && "border",
        !borderless && !errored && !hinted && FIELD_BORDER.default,
        !borderless && !errored && hinted && FIELD_BORDER.hinted,
        !borderless && errored && FIELD_BORDER.errored,
        disabled && "cursor-not-allowed bg-gray-50 text-gray-500",
        !disabled && "cursor-pointer bg-white",
      )}
      data-testid="date-time-picker-button"
      tabIndex={disabled ? -1 : 0}
    >
      <Icon
        icon={faCalendar}
        size="xs"
        variant={disabled ? "disabled" : "default"}
      />
      <div
        className={twJoin(
          "flex-1",
          disabled && "text-gray-400",
          !disabled && !value && "text-gray-500",
          !disabled && !errored && value && hinted && FIELD_TEXT.hinted,
        )}
      >
        {value ? format(value, formatString) : <span>{placeholder}</span>}
      </div>
      {onReset && value && !disabled && (
        <IconButton
          icon={faXmark}
          size="xs"
          onClick={(e) => {
            e.stopPropagation();
            onReset();
          }}
        />
      )}
    </div>
  );

  return (
    <div className={twJoin("w-full", disabled && "pointer-events-none")}>
      <Popover
        button={button}
        className={isInFloatingWindow ? "z-[60]" : "z-10"}
        dataLoc="date-time-picker"
        placement="bottom"
        wrapperClassName={fullWidth ? "w-full" : undefined}
      >
        <DateTimePicker
          dateOnly={dateOnly}
          disabled={disabled}
          value={value}
          {...props}
        />
      </Popover>
    </div>
  );
};
